import { t } from '@ctcells/i18n'

import { TrayFigure, type TrayCellView } from '../../figures/TrayFigure.tsx'
import { FormHint } from '../formSections.tsx'
import { TRAY_SLOTS } from '../monitoring/monitoringModel.ts'
import { recipeSampleTrayCells } from '../templates/recipeComposition.ts'

/**
 * 작업 «구성»을 그리는 부품 — 작업 추가 창(`NewBatchScreen`)과 작업 상세 서랍(`BatchDetailScreen`)이 **같이 쓴다**
 * (eric 2026-10-22 「작업 선택했을 때 상세 내용이... 작업 추가의 내용이 보이도록 해줘」).
 *
 * 🔴 **고르는 손잡이는 여기 없다** — 라디오 · [상세] · [제거]는 추가 창이 열 하나를 덧붙여 단다. 상세는 이것만 쓴다(읽기 전용).
 *    복붙으로 두 벌을 만들지 마라 — 한쪽만 고치면 «만들 때 본 것»과 «만든 뒤 보는 것»이 갈린다.
 * 표의 열 · 시료 번호 줄임은 `batchPlanColumns.tsx` 다(컴포넌트가 아닌 것은 따로 둔다).
 */

/** 트레이 그림의 칸 묶음 — 레시피의 시료 자리(`P1` ↔ `A1 · B1`). 추가 창은 서버 요약, 상세는 레시피 본문에서 온다 */
export interface TrayPlanGroup {
  sample_ref: string
  wells: readonly string[]
}

/**
 * 시료 구역의 트레이 그림 — 레시피의 시료 자리(P1~P4 · REF)를 칸에 적는다(레시피 추가 창 「트레이」와 같은 그림 · `TrayFigure`).
 * 칸 이름(P1 · REF) 아래에 그 웰에 든 시료 번호를 작게 적는다(추가 창 = 사람이 웰마다 고른 것 · 상세 = 서버 배정).
 * 자리를 모르면 빈 9칸(+ 까닭 한 줄이 있으면 그것). 웰 이름이 트레이 밖이면 그림 대신 자리 · 칸 글자.
 *
 * 추가 창만 `onWellPick` 을 준다 — 시료 자리 웰이 단추가 되고(eric 2026-10-27 「해당 칸 또는 웰 별로」), 누르면 부르는 쪽이 그림 오른쪽에
 * 시료 선택 창을 세운다. 상세는 안 준다 — 종전 그대로 읽기 전용 그림이다.
 */
export function BatchTray({
  groups,
  captionOf,
  reason,
  cells: fallbackCells,
  onWellPick,
  openWell,
  wellLabel,
  groupFrames = false,
}: {
  /** 레시피의 시료 자리. 모르면 `undefined` */
  groups: readonly TrayPlanGroup[] | undefined
  /** 그 웰(P1 · REF)에 든 시료 번호(줄인 꼴) — 없으면 칸 이름만 */
  captionOf: (sampleRef: string, wellName: string) => string | undefined
  /** 그림 아래 한 줄(받는 중 · 못 받음 · 레시피 안 고름) */
  reason?: string | undefined
  /** 자리를 모를 때 대신 그릴 칸(상세 — 서버 배정 웰만 «있음»). 안 주면 빈 9칸 */
  cells?: readonly TrayCellView[] | undefined
  /** 시료 자리 웰을 눌렀다(추가 창만). 둘째 인자 = 누른 칸 — 선택 창 자리 · 닫은 뒤 포커스 복귀 */
  onWellPick?: ((wellName: string, target: SVGGElement) => void) | undefined
  /** 지금 시료 선택 창이 열린 웰 — 그 웰 단추가 「펼침」(`aria-expanded`)이고 테가 강조색이다 */
  openWell?: string | undefined
  /** 웰 단추의 접근 이름(「P2 · A2 · 비어 있음」) */
  wellLabel?: ((sampleRef: string, wellName: string) => string) | undefined
  /** 같은 묶음 칸을 한 틀로 감싼다(추가 창만 · eric 2026-10-29) — 상세 서랍은 안 준다(종전 그림 그대로) */
  groupFrames?: boolean | undefined
}) {
  const cells: readonly TrayCellView[] | undefined =
    groups === undefined || groups.length === 0
      ? (fallbackCells ?? TRAY_SLOTS.map((slot) => ({ slot, present: false })))
      : recipeSampleTrayCells(groups, TRAY_SLOTS)?.map((cell) => {
          const caption = cell.label === undefined ? undefined : captionOf(cell.label, cell.slot)
          return caption === undefined ? cell : { ...cell, caption }
        })
  const label =
    groups === undefined || groups.length === 0
      ? t('batchNew.capacityHint')
      : groups.map((g) => `${g.sample_ref} ${g.wells.join(', ')}`).join(' · ')
  /* 묶음 틀 — 웰이 둘 이상인 묶음만(한 칸짜리 REF 는 감쌀 짝이 없다). 색 번호는 그 묶음 칸의 것 */
  const frames =
    !groupFrames || groups === undefined || cells === undefined
      ? undefined
      : groups
          .filter((g) => g.wells.length >= 2)
          .map((g) => ({
            label: g.sample_ref,
            wells: g.wells,
            group: cells.find((c) => c.slot === g.wells[0])?.group,
          }))
  /* 레시피의 시료 자리인 웰만 누른다 — RSLOT · 빈 칸은 고를 것이 없다 */
  const pickable =
    onWellPick === undefined || groups === undefined || groups.length === 0 ? undefined : onWellPick
  return (
    <div className="nbtray__fig">
      {cells === undefined ? (
        <p className="nbtray__text">{label}</p>
      ) : (
        <TrayFigure
          cells={cells}
          chamberOutline
          {...(frames === undefined ? {} : { groupFrames: frames })}
          label={label}
          {...(pickable === undefined
            ? {}
            : {
                onCellPick: pickable,
                openPick: openWell,
                cellPickLabel: (cell: TrayCellView) =>
                  cell.label === undefined || !groups?.some((g) => g.wells.includes(cell.slot))
                    ? undefined
                    : (wellLabel?.(cell.label, cell.slot) ?? `${cell.label} · ${cell.slot}`),
              })}
        />
      )}
      {reason === undefined ? null : <FormHint role="status">{reason}</FormHint>}
    </div>
  )
}
